import { useEffect, useRef, useState } from "react";
import type { BootstrapResponse } from "../../../api/schemas";
import { isIsolatedLocalExecution } from "../../../api/action-capabilities";
import type { WalletSessionController, WalletSessionSnapshot } from "../../../wallet/wallet-session";
import type { Eip1193Provider } from "../../../wallet/eip1193";
import type { RedeemFeeRoute } from "../../../wallet/redeem-fee-quote";
import { resolveRedeemFeeRoute } from "../../../wallet/redeem-fee-route";
import { ContextChangedError } from "../../../wallet/wallet-operation";
import type { ActionContext } from "../../../state/action-context";
import { errorMessage } from "../../../state/model-helpers";
import { ELYSIUM } from "../../../config/elysium";

/** Shared default composition: discover observations, never manufacture a write capability. */
export function useRedeemFeeRoute({ walletController, walletSnapshot, bootstrap, sourceToken, injectedRoute,
  contextKey, readContextKey, refreshRevision }: Pick<ActionContext, "contextKey" | "readContextKey"> & {
  walletController: WalletSessionController; walletSnapshot: WalletSessionSnapshot;
  bootstrap: BootstrapResponse | null; sourceToken: string | null; injectedRoute: RedeemFeeRoute | null;
  refreshRevision: number;
}) {
  const [stored, setStored] = useState<{ key: string; provider: Eip1193Provider | null;
    route: RedeemFeeRoute | null; unavailable: string | null } | null>(null);
  const generation = useRef(0);
  const provider = walletController.getProvider();
  const inputs = useRef({ bootstrap, sourceToken, injectedRoute, provider, authenticated: walletSnapshot.authenticated });
  inputs.current = { bootstrap, sourceToken, injectedRoute, provider, authenticated: walletSnapshot.authenticated };
  // Include read-relevant configuration even if a caller's outer scope has not yet changed.
  const key = JSON.stringify([contextKey, bootstrap?.contracts, bootstrap?.deployment, bootstrap?.tokens, bootstrap?.capabilities, bootstrap?.forkTestOnly,
    bootstrap?.environment, sourceToken, refreshRevision, walletSnapshot.authenticated, injectedRoute]);
  const currentKey = useRef(key);
  currentKey.current = key;
  const legacy = isIsolatedLocalExecution(bootstrap);
  const direct = bootstrap?.contracts.mintChainId === ELYSIUM.chainId;
  useEffect(() => {
    // The key owns invalidation; equal bootstrap objects from polls share work.
    // Read the configuration snapshot for this key when starting a new flight.
    const { bootstrap, sourceToken, injectedRoute, provider, authenticated } = inputs.current;
    const serial = ++generation.current;
    if (injectedRoute || legacy || direct) return;
    const abort = new AbortController();
    const guard = () => {
      abort.signal.throwIfAborted();
      if (serial !== generation.current || key !== currentKey.current || provider !== inputs.current.provider ||
        contextKey !== readContextKey()) throw new ContextChangedError();
    };
    if (!bootstrap || !sourceToken || !provider || !authenticated) {
      setStored({ key, provider, route: null, unavailable: "Connect and sign in before reading the redemption route." });
      return () => abort.abort();
    }
    resolveRedeemFeeRoute(provider, bootstrap, sourceToken, guard, { signal: abort.signal }).then(route => {
      guard();
      setStored({ key, provider, route, unavailable: null });
    }).catch((error: unknown) => {
      if (abort.signal.aborted || serial !== generation.current || key !== currentKey.current ||
        provider !== inputs.current.provider || contextKey !== readContextKey()) return;
      setStored({ key, provider, route: null, unavailable: errorMessage(error) });
    });
    return () => abort.abort();
  }, [key, contextKey, readContextKey, walletController, provider, legacy, direct]);
  if (direct) return { route: injectedRoute, unavailable: null };
  if (injectedRoute) return { route: injectedRoute, unavailable: null };
  if (legacy) return { route: null, unavailable: null };
  return stored?.key === key && stored.provider === provider ? { route: stored.route, unavailable: stored.unavailable } :
    { route: null, unavailable: "The redemption fee route is being verified. Refresh or select the execution network if unavailable." };
}
